<template>
  <VTDropdownPill
    :should-close-on-outside-click="!isDateSelectorOpen"
    :should-close="closeDropdown"
    :close-on-save="false"
    :is-enabled="isEditable"
    @confirm="handleSave"
    @close="closeDropdown = false"
    @clear="handleReset"
    @click="handlePropagation"
  >
    <template #dropDownHeader>
      <div :class="[$style.dateRange, !isEditable ? $style.noHover : '']">
        <span :class="$style.label">From</span>
        <span :class="$style.iconWrapper">
          <VdIcon store="fa" name="calendar" color="#909298" />
        </span>
        <span :class="$style.date">
          {{ formattedStartDate }}
        </span>
        <span :class="$style.label">To</span>
        <span :class="$style.iconWrapper">
          <VdIcon store="fa" name="calendar" color="#909298" />
        </span>
        <span>
          {{ formattedEndDate }}
        </span>
        <VdIcon v-if="isEditable" :class="$style.editIcon" store="fa" name="chevron-down" />
      </div>
    </template>
    <template #dropdown-content>
      <div @click="handlePropagation">
        <div :class="$style.calendarSection">
          <div>Start</div>
          <InputGroup icon-position="left">
            <InputGroupAddon>
              <i class="pi pi-calendar" />
            </InputGroupAddon>
            <DatePicker
              id="durationStart"
              v-model="form.startDateEdit"
              date-format="dd/mm/yy"
              placeholder="Start date"
              :min-date="minStartDate"
              @show="handleShow"
              @hide="handleHide"
            />
          </InputGroup>
          <div v-if="validationErrors.startDateEdit" :class="$style.error">{{ validationErrors.startDateEdit }}</div>
        </div>

        <div :class="$style.calendarSection">
          <div>End</div>
          <InputGroup icon-position="left">
            <InputGroupAddon>
              <i class="pi pi-calendar" />
            </InputGroupAddon>
            <DatePicker
              id="durationEnd"
              v-model="form.endDateEdit"
              date-format="dd/mm/yy"
              placeholder="End date"
              :min-date="new Date()"
              @show="handleShow"
              @hide="handleHide"
            />
          </InputGroup>
          <div v-if="validationErrors.endDateEdit" :class="$style.error">{{ validationErrors.endDateEdit }}</div>
        </div>
      </div>
    </template>
  </VTDropdownPill>
</template>

<script setup>
import VTDropdownPill from "@/components/VTDropdownPill";
import VdIcon from "@/components/VdIcon/VdIcon.vue";
import formatDateTime from "@/utils/formatDateTime";
import { isBefore, startOfDay } from "date-fns";
import DatePicker from "primevue/datepicker";
import InputGroup from "primevue/inputgroup";
import InputGroupAddon from "primevue/inputgroupaddon";
import { computed, reactive, ref } from "vue";
import * as yup from "yup";

const props = defineProps({
  startDate: String,
  endDate: String,
  isEditable: Boolean,
});

const emit = defineEmits(["updateDates"]);

const form = reactive({
  startDateEdit: new Date(props.startDate),
  endDateEdit: new Date(props.endDate),
});

const validationErrors = reactive({ startDateEdit: null, endDateEdit: null });

const isDateSelectorOpen = ref(false);

const closeDropdown = ref(false);

const formattedStartDate = computed(() => formatDateTime(props.startDate));
const formattedEndDate = computed(() => formatDateTime(props.endDate));

const minStartDate = computed(() => {
  const today = startOfDay(new Date());

  const propsStartDate = startOfDay(new Date(props.startDate));

  return isBefore(propsStartDate, today) ? propsStartDate : today;
});

function handlePropagation(event) {
  event.stopPropagation();
}

function handleShow() {
  isDateSelectorOpen.value = true;
}

function handleHide() {
  isDateSelectorOpen.value = false;
}

const schema = computed(() =>
  yup.object({
    startDateEdit: yup
      .date()
      .required("Start date is required")
      .min(startOfDay(new Date()), "Start date must be after today's date"),
    endDateEdit: yup
      .date()
      .required("End date is required")
      .min(startOfDay(form.startDateEdit || new Date()), "End date must be after or equal to start date"),
  })
);

async function handleSave() {
  try {
    await schema.value.validate(form, { abortEarly: false });
    validationErrors.startDateEdit = null;
    validationErrors.endDateEdit = null;
    emit("updateDates", { startDate: form.startDateEdit, endDate: form.endDateEdit });
    closeDropdown.value = true;
  } catch (err) {
    const validationErrorsArray = err.inner;
    validationErrorsArray.forEach((error) => {
      validationErrors[error.path] = error.message;
    });
    closeDropdown.value = false;
  }
}

function handleReset() {
  form.startDateEdit = new Date(props.startDate);
  form.endDateEdit = new Date(props.endDate);
  validationErrors.startDateEdit = null;
  validationErrors.endDateEdit = null;
  closeDropdown.value = true;
}
</script>

<style module>
.dateRange {
  display: flex;
  align-items: center;
  font-size: 14px;
  padding: 8px;
  transition: background-color 0.3s;
  border-radius: 20px;
}

.dateRange:hover {
  background-color: var(--vd-color-surface-100);
}

.iconWrapper {
  margin-right: 4px;
}

.label {
  font-size: 14px;
  font-weight: 600;
  color: #909298;
  margin-right: 10px;
}

.date {
  margin-right: 15px;
}

.editIcon {
  margin-left: 0.5rem;
}

.calendarSection {
  text-align: left;
  margin-bottom: 1rem;
}

.error {
  font-size: 12px;
  color: #f87171;
  margin-top: 0.5rem;
}

.noHover:hover {
  background-color: transparent;
}
</style>
